import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function NewInvoice() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialPatient = location.state?.patient || null;
  const [patient, setPatient] = useState(initialPatient);
  const [patientQuery, setPatientQuery] = useState("");
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([{ description: "OPD Consultation", qty: 1, rate: 500, gst_percent: 0, discount: 0 }]);
  const [discount, setDiscount] = useState(0);
  const [mode, setMode] = useState("cash");
  const [paid, setPaid] = useState(0);
  const [payerCategory, setPayerCategory] = useState("cash");
  const [panelName, setPanelName] = useState("");
  const [policyNo, setPolicyNo] = useState("");
  const [tpaName, setTpaName] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => { api.get("/masters/services").then(({ data }) => setServices(data.items)); }, []);

  const searchPatients = async (q) => {
    setPatientQuery(q);
    if (q.length < 2) { setPatients([]); return; }
    const { data } = await api.get("/patients", { params: { q, limit: 8 } });
    setPatients(data.items);
  };

  const totals = useMemo(() => {
    let gross = 0, tax = 0, itemDisc = 0;
    for (const it of items) {
      const lg = (it.qty || 0) * (it.rate || 0);
      const ld = it.discount || 0;
      gross += lg;
      itemDisc += ld;
      tax += (lg - ld) * ((it.gst_percent || 0) / 100);
    }
    const dt = itemDisc + (Number(discount) || 0);
    const net = gross - dt;
    const total = net + tax;
    const balance = Math.max(0, total - (Number(paid) || 0));
    return { gross, tax, discountTotal: dt, net, total, balance };
  }, [items, discount, paid]);

  const updateItem = (i, k, v) => {
    const arr = [...items]; arr[i] = { ...arr[i], [k]: ["qty","rate","gst_percent","discount"].includes(k) ? Number(v) : v }; setItems(arr);
  };
  const addLine = () => setItems([...items, { description: "", qty: 1, rate: 0, gst_percent: 0, discount: 0 }]);
  const removeLine = (i) => setItems(items.filter((_, idx) => idx !== i));
  const addService = (sid) => {
    const s = services.find(x => x.id === sid);
    if (s) setItems([...items, { description: s.name, qty: 1, rate: s.price, gst_percent: s.gst_percent, discount: 0 }]);
  };

  const save = async () => {
    if (!patient) { toast.error("Select a patient first"); return; }
    if (items.length === 0) { toast.error("Add at least one line"); return; }
    try {
      const { data } = await api.post("/billing/invoices", {
        patient_id: patient.id, items, discount_total: Number(discount) || 0,
        payment_mode: mode, amount_paid: Number(paid) || 0, notes,
        payer_category: payerCategory,
        panel_name: payerCategory !== "cash" && payerCategory !== "free" ? panelName : "",
        policy_no: payerCategory !== "cash" && payerCategory !== "free" ? policyNo : "",
        tpa_name: payerCategory === "tpa" ? tpaName : "",
      });
      toast.success(`${data.invoice_no} created`);
      navigate(`/app/billing/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-24">
      <div>
        <div className="section-label">Cashier</div>
        <h1 className="font-heading text-3xl font-semibold">New invoice</h1>
      </div>

      <Card className="p-5">
        <div className="section-label mb-3">Patient</div>
        {patient ? (
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="font-medium">{patient.name}</div>
              <div className="text-xs text-ink-muted font-mono">{patient.uhid} · {patient.mobile}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPatient(null)}>Change</Button>
          </div>
        ) : (
          <div>
            <Input value={patientQuery} onChange={e => searchPatients(e.target.value)} placeholder="Search patient by name / UHID / mobile…" data-testid="invoice-patient-search" />
            {patients.length > 0 && (
              <div className="mt-2 max-h-60 overflow-auto rounded-md border border-border">
                {patients.map(p => (
                  <button key={p.id} onClick={() => { setPatient(p); setPatients([]); setPatientQuery(""); }}
                    className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary"
                    data-testid={`invoice-patient-${p.uhid}`}>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-ink-muted">{p.uhid} · {p.mobile}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="section-label">Line items</div>
          <div className="flex gap-2">
            <Select onValueChange={addService}>
              <SelectTrigger className="w-56"><SelectValue placeholder="+ add from services master" /></SelectTrigger>
              <SelectContent>
                {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · ₹{s.price}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={addLine}><Plus size={12} className="mr-1" />Custom line</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Rate</th>
                <th className="px-3 py-2 text-right">GST %</th>
                <th className="px-3 py-2 text-right">Discount</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const lg = (it.qty || 0) * (it.rate || 0);
                const ld = it.discount || 0;
                const lt = (lg - ld) * (1 + (it.gst_percent || 0)/100);
                return (
                  <tr key={i} className="border-b border-border">
                    <td className="px-3 py-2"><Input value={it.description} onChange={e => updateItem(i, "description", e.target.value)} /></td>
                    <td className="px-3 py-2"><Input type="number" className="text-right" value={it.qty} onChange={e => updateItem(i, "qty", e.target.value)} /></td>
                    <td className="px-3 py-2"><Input type="number" className="text-right" value={it.rate} onChange={e => updateItem(i, "rate", e.target.value)} /></td>
                    <td className="px-3 py-2"><Input type="number" className="text-right" value={it.gst_percent} onChange={e => updateItem(i, "gst_percent", e.target.value)} /></td>
                    <td className="px-3 py-2"><Input type="number" className="text-right" value={it.discount} onChange={e => updateItem(i, "discount", e.target.value)} /></td>
                    <td className="px-3 py-2 text-right font-medium">₹ {lt.toFixed(2)}</td>
                    <td className="px-3 py-2"><Button variant="ghost" size="icon" onClick={() => removeLine(i)}><Trash2 size={14} className="text-rose-500" /></Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="section-label mb-3">Payer & payment</div>
          <div className="form-grid">
            <div>
              <Label>Payer category</Label>
              <Select value={payerCategory} onValueChange={(v) => {
                setPayerCategory(v);
                // Reset paid when switching to a panel category so it defaults to ₹0
                if (["panel", "insurance", "corporate", "tpa", "camp"].includes(v)) setPaid(0);
              }}>
                <SelectTrigger data-testid="invoice-payer-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "panel", "insurance", "corporate", "tpa", "camp", "free"].map(c => (
                    <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {["panel", "insurance", "corporate", "tpa", "camp"].includes(payerCategory) && (
                <div className="mt-1 text-[10px] text-amber-700">
                  Final bill will be marked as pending settlement — no payment receipt until panel pays.
                </div>
              )}
            </div>
            <div>
              <Label>Payment mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash","upi","card","bank_transfer","cheque","insurance","panel"].map(m => <SelectItem key={m} value={m} className="capitalize">{m.replace("_"," ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Amount paid (₹)</Label><Input type="number" value={paid} onChange={e => setPaid(Number(e.target.value))} data-testid="invoice-paid-input" /></div>
            <div><Label>Additional discount (₹)</Label><Input type="number" value={discount} onChange={e => setDiscount(Number(e.target.value))} /></div>
            {["panel", "insurance", "corporate", "tpa", "camp"].includes(payerCategory) && (
              <>
                <div>
                  <Label>Panel / Insurer name</Label>
                  <Input value={panelName} onChange={e => setPanelName(e.target.value)} placeholder="e.g. Star Health Insurance" data-testid="invoice-panel-name" />
                </div>
                <div>
                  <Label>Policy / Card no.</Label>
                  <Input value={policyNo} onChange={e => setPolicyNo(e.target.value)} placeholder="e.g. SH-12345" data-testid="invoice-policy-no" />
                </div>
                {payerCategory === "tpa" && (
                  <div>
                    <Label>TPA name</Label>
                    <Input value={tpaName} onChange={e => setTpaName(e.target.value)} placeholder="e.g. Medi Assist" data-testid="invoice-tpa-name" />
                  </div>
                )}
              </>
            )}
            <div className="md:col-span-3"><Label>Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} /></div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="section-label mb-3">Summary</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-ink-muted">Gross</span><span>₹ {totals.gross.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-ink-muted">Discount</span><span>− ₹ {totals.discountTotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-ink-muted">Net</span><span>₹ {totals.net.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-ink-muted">GST</span><span>+ ₹ {totals.tax.toFixed(2)}</span></div>
            <div className="my-2 border-t border-border" />
            <div className="flex justify-between font-heading text-lg"><span>Total</span><span className="text-brand">₹ {totals.total.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs text-ink-muted"><span>Balance</span><span>₹ {totals.balance.toFixed(2)}</span></div>
          </div>
        </Card>
      </div>

      <div className="sticky bottom-0 -mx-4 flex items-center justify-between border-t border-border bg-white px-4 py-3 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.05)] md:-mx-6 lg:-mx-8">
        <div className="text-xs text-ink-muted">GST-ready invoice · audit-logged</div>
        <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="invoice-save-btn">Create invoice</Button>
      </div>
    </div>
  );
}
